import { defineStore, acceptHMRUpdate } from 'pinia';
import { useMetaGroupsStore } from '@/stores/meta-groups.store';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import { useOrganizationStore } from '@/stores/organization.store';
import { createFrozenMap } from '@/vuex/utils';
import { fetchLayouts, fetchOrganizationLayouts, deleteLayout, fetchLayoutCategories } from '@/stores/api/layouts.api';
import { createUnassignedMetaGroup } from '@/stores/helpers/layouts.helper';
import { localizeDefaultGuideCategories } from '@/stores/helpers/guide-categories.helper';
import { sortByName } from '@/stores/helpers/guide-categories.helper';
import { isWebBasedApp, isMobileApp, isEmailPlatform } from '@/utils/apps';

import { useSubscriptionStore } from '@/stores/subscription.store';

function getInitialState () {
    return {
        map: {} as Record<string, unknown>,
        categoryMap: {} as Record<string, unknown>,
    };
}

export const useLayoutsStore = defineStore('layouts', {
    state: getInitialState,
    getters: {
        list: (state) => values(state.map),
        listForPlatform () {
            return (platform: string, excludeMultiStep = false) => {
                const isMobile = isMobileApp(platform);
                const isWebApp = isWebBasedApp(platform);
                const isEmail = isEmailPlatform(platform);

                return (this.list as Record<string, unknown>[]).filter(({ supportedPlatforms, steps }) => {
                    if (excludeMultiStep && (steps as unknown[])?.length > 1) return false;

                    if (!supportedPlatforms) {
                        return isWebApp;
                    }

                    if (isWebApp && !(supportedPlatforms as Record<string, unknown>).web) {
                        return false;
                    }

                    if (isMobile && !(supportedPlatforms as Record<string, unknown>).mobile) {
                        return false;
                    }

                    if (isEmail && !(supportedPlatforms as Record<string, unknown>).web && !(supportedPlatforms as Record<string, unknown>).email) {
                        return false;
                    }

                    return true;
                });
            };
        },
        layoutCategories: (state) => values(state.categoryMap),
        layoutCategoriesGroupedByMetaGroup () {
            return (platform: string) => {
                const shouldIncludeCategory = (category: Record<string, unknown> | undefined) => {
                    if (!category) return false;
                    if ((category.attributes as Record<string, unknown>)?.platform !== platform) return false;

                    return true;
                };
                const groupedCategoryIds = new Set<string>();

                const metaGroupsWithCategories = sortByName(useMetaGroupsStore().list).map((metaGroup: Record<string, unknown>) => {
                    const items = (metaGroup.items as Array<{ itemId: string }>)
                        .map((item) => {
                            groupedCategoryIds.add(item.itemId);

                            return (this.categoryMap as Record<string, unknown>)[item.itemId] as Record<string, unknown>;
                        })
                        .filter(shouldIncludeCategory);

                    return { ...metaGroup, items: sortByName(items) };
                });

                const unassignedCategories = this.layoutCategories.filter((category: unknown) =>
                    !groupedCategoryIds.has((category as Record<string, unknown>).id as string) && shouldIncludeCategory(category as Record<string, unknown>));
                const unassignedMetaGroup = createUnassignedMetaGroup(unassignedCategories);

                metaGroupsWithCategories.push(unassignedMetaGroup);

                return metaGroupsWithCategories;
            };
        },
    },
    actions: {
        setLayouts ({ layouts }: { layouts: unknown[] }) {
            this.map = keyBy(layouts, 'id') as Record<string, unknown>;
        },
        setLayout ({ layout }: { layout: Record<string, unknown> }) {
            this.map = {
                ...this.map,
                [layout.id as string]: layout,
            };
        },
        removeLayoutFromMap ({ id }: { id: string }) {
            delete this.map[id];
        },
        setLayoutCategories ({ categories }: { categories: Record<string, unknown> }) {
            this.categoryMap = categories;
        },
        async loadAll ({ reFetch = false } = {}) {
            if (Object.keys(this.map).length && !reFetch) {
                return;
            }

            const activeOrganization = useOrganizationStore().getActive;
            const orgId = get(activeOrganization, 'id', null);
            const activeSubscription = useSubscriptionStore().getActiveSub;
            const subId = get(activeSubscription, 'id', null);
            const includeOrganizationResources = get(
                activeOrganization,
                'organizationFlags.enableResourceSharing',
                false,
            );

            const requests = [fetchLayouts()];
            if (orgId && includeOrganizationResources) {
                requests.push(fetchOrganizationLayouts(orgId, subId));
            }
            const promises = await Promise.all(requests);
            const layouts = promises.reduce((prevPromise, currPromise) => (prevPromise as unknown[]).concat(currPromise)) as unknown[];

            this.setLayouts({ layouts });
        },
        async delete ({ layout }: { layout: Record<string, unknown> }) {
            await deleteLayout(layout.id as string);

            this.removeLayoutFromMap({ id: layout.id as string });
        },
        async loadCategories ({ items = false } = {}) {
            const { data: categories } = await fetchLayoutCategories({ items });
            const localizedCategories = localizeDefaultGuideCategories(categories);
            this.setLayoutCategories({
                categories: createFrozenMap(localizedCategories) as Record<string, unknown>,
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useLayoutsStore, module.hot));
}
